import React, { useState, useRef, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Form } from "react-bootstrap";
import { FaEye } from "react-icons/fa";
import useAuth from "./../hooks/useAuth";
import Preloader from "../shared/PreLoaders";
import { logActivity } from "../utils/LogActivity";
import { AuthContext } from "../Context/AuthContext";

const Login = () => {
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const { setAuth, fetchPermissions } = useAuth();
  const { userId } = useContext(AuthContext);
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpArray, setOtpArray] = useState(Array(6).fill(""));
  const [timer, setTimer] = useState(120);
  const Api = process.env.REACT_APP_API;
  const inputRefs = useRef([]);

  useEffect(() => {
    if (otpSent && timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(countdown);
    }
  }, [otpSent, timer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials({ ...credentials, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${Api}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        throw new Error("Login failed. Please check your email and password.");
      }

      setOtpSent(true);
      setTimer(180);
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const handleOtpChange = (value, index) => {
    if (/^\d$/.test(value)) {
      const updatedOtpArray = [...otpArray];
      updatedOtpArray[index] = value;
      setOtpArray(updatedOtpArray);

      // Move focus to the next input if available
      if (index < otpArray.length - 1) {
        inputRefs.current[index + 1].focus();
      }
    } else if (value === "") {
      // Handle backspace
      const updatedOtpArray = [...otpArray];
      updatedOtpArray[index] = "";
      setOtpArray(updatedOtpArray);

      // Move focus to the previous input if available
      if (index > 0) {
        inputRefs.current[index - 1].focus();
      }
    }
  };

  // Handle Paste Event
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    if (/^\d{6}$/.test(pastedData)) {
      const updatedOtpArray = pastedData.split("");
      setOtpArray(updatedOtpArray);

      // Move focus to the last field
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const otp = otpArray.join("");
      const response = await fetch(`${Api}/api/auth/verify-login-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: credentials.email, otp }),
      });

      if (!response.ok) {
        throw new Error("OTP verification failed");
      }

      const data = await response.json();
      setAuth(data);

      localStorage.setItem("authData", JSON.stringify(data));
      sessionStorage.setItem("authData", JSON.stringify(data));
      sessionStorage.setItem("authToken", data.token);

      fetchPermissions(data.token);
      await logActivity(userId, "User Logged in", "Login Page", Api);
      navigate("/dashboard");
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  return (
    <div>
      <Preloader loading={loading} />
      <div className="d-flex align-items-center auth px-0 ">
        <div className="row w-100 mx-0 ">
          <div className="col-lg-4 mx-auto container">
            <div className="auth-form-light text-left py-5 px-5 px-sm-5">
              <div className="brand-logo">
                <img src={require("../../assets/images/Logo.png")} alt="logo" />
              </div>
              <h4>Hello! let's get started</h4>
              <h6 className="font-weight-light">
                {otpSent ? "Verify OTP to continue." : "Sign in to continue."}
              </h6>
              <Form
                className="pt-3"
                onSubmit={otpSent ? handleVerifyOTP : handleSubmit}
              >
                {error && <p className="text-danger">{error}</p>}

                {!otpSent && (
                  <div>
                    <Form.Group className="d-flex search-field position-relative">
                      <Form.Control
                        type="email"
                        placeholder="Email"
                        size="lg"
                        className="h-auto"
                        name="email"
                        value={credentials.email}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="d-flex search-field position-relative mt-3">
                      <Form.Control
                        type={passwordVisible ? "text" : "password"}
                        placeholder="Password"
                        size="lg"
                        className="h-auto"
                        name="password"
                        value={credentials.password}
                        onChange={handleChange}
                        required
                      />

                      <FaEye
                        onMouseDown={() => setPasswordVisible(true)}
                        onMouseUp={() => setPasswordVisible(false)}
                        onMouseLeave={() => setPasswordVisible(false)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                        }}
                      />
                    </Form.Group>
                  </div>
                )}

                {otpSent && (
                  <>
                    <div>
                      <div id="timer">
                        Time remaining: {Math.floor(timer / 60)}:
                        {String(timer % 60).padStart(2, "0")}
                      </div>
                      <div className="otp-input">
                        {otpArray.map((digit, index) => (
                          <input
                            key={index}
                            ref={(el) => (inputRefs.current[index] = el)}
                            type="text"
                            value={digit}
                            onChange={(e) =>
                              handleOtpChange(e.target.value, index)
                            }
                            onPaste={handlePaste}
                            maxLength="1"
                          />
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <div className="mt-3">
                  <button
                    type="submit"
                    className="btn btn-blocks btn-lg font-weight-medium auth-form-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Processing..."
                      : otpSent
                      ? "Verify OTP"
                      : "SIGN IN"}
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
